// DocumentService.js
import api from './Api'; // Import the pre-configured axios instance

const DocumentService = {
    /**
     * Fetches documents for a specific folder from the backend using Axios.
     * This method uses folderId, but you can adapt it to use folderPath
     * if your API expects that (e.g., /documents?path=/my/folder).
     * @param {string} folderId The ID of the folder.
     * @returns {Promise<Array<Object>>} A promise that resolves to an array of document objects.
     */
    fetchDocuments: async (folderId, folderPath) => {
        try {
            // Assuming your API endpoint for documents within a folder is /folders/{folderId}/documents
            // If your API uses folderPath, you might change this to:
            // const response = await api.get('/documents', { params: { path: folderPath } });
            const response = await api.get(`/folders/${folderId}/${folderPath}/documents`);
            return response.data; // Axios automatically parses JSON into .data
        } catch (error) {
            console.error(`Error in fetchDocuments for folder ${folderId}:`, error.response ? error.response.data : error.message);
            throw error;
        }
    },

    /**
     * Uploads a document to a specific folder on the backend using Axios.
     * @param {string} folderId The ID of the folder to upload to.
     * @param {File} file The file object to upload.
     * @returns {Promise<Object>} A promise that resolves to the uploaded document object.
     */
    uploadDocument: async (folderParentPath, file) => {
        try {
            const formData = new FormData();
            formData.append('file', file); // 'file' should match the field name your API expects
            formData.append('parentPath', folderParentPath); // Include folderId if your API expects it in FormData

            const response = await api.post('/documents', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data', // Axios handles this automatically for FormData, but good to be explicit
                    // 'Authorization': `Bearer YOUR_AUTH_TOKEN`, // Add authorization header if needed
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error in uploadDocument:', error.response ? error.response.data : error.message);
            throw error;
        }
    },

    /**
     * Deletes a document from the backend using Axios.
     * @param {string} documentId The ID of the document to delete.
     * @returns {Promise<void>} A promise that resolves upon successful deletion.
     */
    deleteDocument: async (documentId) => {
        try {
            // Assuming your delete endpoint is /documents/{documentId}
            await api.delete(`/documents/${documentId}`);
            console.log(`Document ${documentId} deleted successfully.`);
        } catch (error) {
            console.error(`Error in deleteDocument ${documentId}:`, error.response ? error.response.data : error.message);
            throw error;
        }
    },

    /**
     * Generates a URL or fetches content for viewing a document.
     * The implementation depends heavily on your backend API.
     * - If your API provides a direct public URL, you can just return that.
     * - If it requires authentication, you might fetch a temporary signed URL or the document content directly.
     * @param {string} documentId The ID of the document to view.
     * @returns {Promise<string>} A promise that resolves to the document's viewable URL or content.
     */
    viewDocument: async (documentId) => {
        try {
            // Option 1: If your API provides a direct public URL for viewing
            // const response = await api.get(`/documents/${documentId}/view-url`);
            // return response.data.url;

            // Option 2: If your API requires fetching the content (e.g., for display in an iframe or blob)
            // This example fetches a blob, useful for displaying images/PDFs directly in the browser
            const response = await api.get(`/documents/${documentId}/content`, {
                responseType: 'blob', // Important for binary data
                // headers: { 'Authorization': `Bearer YOUR_AUTH_TOKEN` } // if authenticated
            });
            return URL.createObjectURL(response.data); // Create a local URL for the blob

        } catch (error) {
            console.error(`Error in viewDocument ${documentId}:`, error.response ? error.response.data : error.message);
            throw error;
        }
    },
};

export default DocumentService;
